<template>
  <div v-loading="loading" class="property-wallet-page">
    <div class="property-wallet-page__header">
      <span class="property-wallet-page__header__total">
        <strong>
          <app-i18n code="entities.propertyWallet.title.accounts" />
        </strong>
        <span v-text="' '" />
        <app-i18n-amount
          :amount="balanceTotal.amount"
          :currency="balanceTotal.currency"
        />
      </span>
      <span
        v-if="connectedAccount"
        class="property-wallet-page__header__payout"
      >
        <app-i18n
          class="property-wallet-page__header__payout__title"
          code="entities.propertyWallet.title.payout"
        />

        <template v-if="payoutsScheduleInterval === 'manual'">
          <span v-html="'&nbsp;'" />
          <app-i18n code="subpropertySettings.finantial.payoutSchedule.cardFrequency.placeholders.manual" />
        </template>

        <template v-else>
          <span v-html="'&nbsp;'" />
          <app-i18n code="subpropertySettings.finantial.payoutSchedule.cardFrequency.placeholders.automatic" />
          <span v-html="' - '" />
          <app-i18n :code="`enumerators.stripePayoutsScheduleInterval.${payoutsScheduleInterval}`" />
          <template v-if="payoutsScheduleAnchorI18nCode">
            <span v-html="'&nbsp;('" />
            <app-i18n :code="payoutsScheduleAnchorI18nCode" />
            <span v-html="')'" />
          </template>
        </template>
      </span>
    </div>

    <simplebar
      data-simplebar
      data-simplebar-force-visible="y"
      data-simplebar-auto-hide="false"
      class="property-wallet-page__accounts__list simplebar--solid"
    >
      <div
        v-for="({ id, name, stripeData }, pos) in subpropertiesWithConnectedAccount"
        :key="id"
        class="property-wallet-page__accounts__list__element"
        :class="{ 'property-wallet-page__accounts__list__element__active': pos === currentPos }"
        @click="doChangeCurrentPos({ currentPos: pos })"
      >
        <div
          class="property-wallet-page__accounts__list__element__logo"
          :style="{ backgroundImage: manageLogoUrl }"
        />
        <span class="property-wallet-page__accounts__list__element__amount">
          <app-i18n-amount
            v-if="stripeData && stripeData.balanceTotal && stripeData.balanceTotal.length"
            :amount="stripeData.balanceTotal[0].amount"
            :currency="stripeData.balanceTotal[0].currency"
          />
          <app-i18n-amount
            v-else
            :amount="0"
            currency="usd"
          />
        </span>
        <span class="property-wallet-page__accounts__list__element__name">
          <app-i18n
            code="entities.propertyWallet.list.wallet"
            :args="[name]"
          />
        </span>
      </div>
    </simplebar>

    <div
      v-loading="loading"
      v-if="!loading"
    >
      <div class="property-wallet-page__accounts__balance__header">
        <span class="property-wallet-page__accounts__balance__header__title">Dashboard</span>
      </div>

      <simplebar
        data-simplebar
        :data-simplebar-force-visible="false"
        data-simplebar-auto-hide="false"
        class="property-wallet-page__accounts__balance simplebar--solid"
      >
        <!-- Balance -->
        <el-container class="property-wallet-page__accounts__balance__container">
          <app-stripe-balance-widget
            v-if="subpropertyId"
            :key="subpropertyId"
            :subpropertyId="subpropertyId"
          />
        </el-container>

        <!-- Payouts -->
        <!-- <el-container
          v-loading="loading"
          v-if="!loading && connectedAccount && connectedAccount.id"
          class="property-wallet-page__accounts__balance__activity__container"
        >
          <app-stripe-payout-list-table
            :key="propertyId"
            :propertyId="propertyId"
            :connectedAccountId="connectedAccount.id"
          />
        </el-container> -->
      </simplebar>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import { mapGetters, mapActions } from 'vuex';

import StripeBalanceWidget from '@modules/stripe/stripe-balance/components/stripe-balance-widget.vue';
import StripePayoutListTable from '@modules/stripe/stripe-payout/components/stripe-payout-list-table.vue';

export default {
  name: 'app-property-wallet-page',

  async created() {
    if (!this.paymentsEnabled) return this.$router.push({ name: 'home' })
    await this.doFetch()
  },

  computed: {
    ...mapGetters({
      currentManage: 'auth/manage/currentManage',
      paymentsEnabled: 'auth/manage/paymentsEnabled',
      connectedAccount: 'stripeBalance/list/connectedAccount',
      subproperties: 'property/wallet/rows',
      currentPos: 'property/wallet/currentPos',
      loading: 'property/wallet/loading',
    }),
    subpropertiesWithConnectedAccount() {
      return this.subproperties.filter(({ stripeData }) => !!stripeData);
    },
    manageLogoUrl() {
      if (!this.currentManage) return null;
      if (
        this.currentManage.property &&
        this.currentManage.property.logos &&
        this.currentManage.property.logos.length > 0
      ) {
        return `url("${this.currentManage.property.logos[0].publicUrl}")`;
      }
      return null;
    },
    propertyId() {
      return this.currentManage.property.id;
    },
    subpropertyId() {
      if (this.currentPos >= 0 && this.currentPos < this.subpropertiesWithConnectedAccount.length) {
        return this.subpropertiesWithConnectedAccount[this.currentPos].id;
      }
      return null;
    },
    balanceTotal() {
      const subproperties = this.subpropertiesWithConnectedAccount.map(({ stripeData }) =>  ({  
        amount: _.get(stripeData, 'balanceTotal[0].amount', 0), 
        currency: _.get(stripeData, 'balanceTotal[0].currency', 'usd'),       
      }));

      if (subproperties.length > 0) {
        const amountSum = subproperties.reduce((sum, { amount }) => sum + amount, 0 );
        return { amount: amountSum, currency: subproperties[0].currency };
      }

      return { amount: 0, currency: 'usd' };
    },
    payoutsScheduleInterval() {
      if (this.connectedAccount && this.connectedAccount.settings) {
        return this.connectedAccount.settings.payoutsScheduleInterval;
      }
      return null;
    },
    payoutsScheduleAnchorI18nCode() {
      const { settings } = this.connectedAccount
      switch (this.payoutsScheduleInterval) {
        case 'weekly':
          return `enumerators.weekDay.${settings.payoutsScheduleWeeklyAnchor}`
        case 'monthly':
          return `enumerators.monthDay.${settings.payoutsScheduleMonthlyAnchor}`
        default:
          return null
      }
    },
  },

  methods: {
    ...mapActions({
      doFetch: 'property/wallet/doFetch',
      doChangeCurrentPos: 'property/wallet/doChangeCurrentPos',
    }),
  },

  components: {
    [StripePayoutListTable.name]: StripePayoutListTable,
    [StripeBalanceWidget.name]: StripeBalanceWidget,
  },
};
</script>

<style lang="scss">
.property-wallet-page {
  &__accounts__list {
    .simplebar-content {
      padding-top: 0 !important;
      padding-bottom: 0 !important;
    }
  }
}
</style>

<style lang="scss" scoped>
.property-wallet-page {
  --page-margin-top: 22px;
  --header-height: 60px;
  --sub-header-height: 52px;
  --page-height: calc(100vh - var(--layout-header-height) - var(--layout-main-padding-vertical) * 3);
  --list-height: calc(var(--page-height) - var(--header-height) - var(--page-margin-top));
  --dashboard-height: calc(var(--list-height) - var(--sub-header-height) + 8px);

  display: grid;
  grid-template-areas:
    'header header'
    'list balance';
  grid-template-columns: 30% 70%;
  background-color: white;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  margin-top: var(--page-margin-top);

  max-height: var(--page-height);
  overflow: hidden;

  &__header {
    display: grid;
    grid-template-areas: 'total payout';
    grid-area: header;
    align-content: center;
    align-items: center;

    padding: 1em 2em;
    background-color: white;
    border-bottom: solid 0.3px rgba(151, 151, 151, 0.9);
    font-size: 18px;
    z-index: 10;
    height: var(--header-height);
    box-sizing: border-box;

    &__total {
      grid-area: total;
      font-size: 18px;
      font-style: normal;
      font-stretch: normal;
      line-height: normal;
      letter-spacing: normal;
      color: #494949;
    }

    &__payout {
      grid-area: payout;

      font-size: 14px;
      font-weight: 500;
      text-align: right;
      color: #404040;

      &__title {
        font-weight: 300;
      }
    }
  }

  &__accounts {
    &__list {
      max-height: var(--list-height);
      grid-area: list;
      padding-top: 8px;
      height: 100vh;

      &__element {
        display: grid;
        grid-template-areas:
          'logo amount'
          '. name';

        cursor: pointer;
        position: relative;
        padding: 2em 1em;
        grid-template-columns: 60px auto;
        grid-template-rows: 40px auto;
        align-items: center;
        outline: none;
        z-index: 0;
        background-color: #f8f8f8;

        &:hover,
        &__active {
          background-color: white;
          box-shadow: 0 2px 4px 0 rgba(206, 206, 206, 0.5);
          z-index: 10;
        }

        &__logo {
          grid-area: logo;
          width: 50px;
          height: 30px;
          justify-self: center;
          background-size: contain;
          background-repeat: no-repeat;
          background-position: center center;
        }

        &__amount {
          grid-area: amount;
          font-size: 30px;
          font-weight: bold;
          color: #282d32;
        }

        &__name {
          grid-area: name;
          font-size: 14px;
          font-weight: normal;
          color: #282d32;
        }
      }
    }

    &__balance {
      grid-area: balance;
      height: 100%;
      max-height: var(--dashboard-height);
      padding: 0 24px;

      &__header {
        height: var(--sub-header-height);
        box-sizing: border-box;
        display: grid;
        grid-template-areas: 'title button';
        border-bottom: solid 0.3px rgba(151, 151, 151, 0.9);
        padding: 16px 24px;
        align-items: center;

        &__title {
          grid-area: title;
          font-size: 14px;
          font-weight: 500;
          font-style: normal;
          line-height: 1.21;
          letter-spacing: 0.2px;
          color: rgba(102, 102, 102, 0.74);
        }

        &__button {
          grid-area: button;
          justify-self: end;
        }
      }

      &__container {
        flex-direction: column;
        box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
        padding: 24px;
        margin: 16px 0;
      }

      &__activity {
        &__container {
          flex-direction: column;
          box-shadow: -10px 10px 20px 0 rgba(30, 30, 30, 0.05);
          margin: 24px 0;
          padding: 24px 0 0;
        }
      }
    }
  }
}
</style>
